import React from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import {
  DEFAULT_OUT_OF_QUOTA_RULE,
  normalizeOption,
  normalizeOutOfQuotaRule,
  computeOutOfQuotaLimit
} from '../../utils/teamAgeValidation';

/**
 * Input età min/max (e opzionalmente max fuori fascia) per una singola opzione.
 */
export const AgeOptionFields = ({ option, onChange, showMaxOutOfQuota = true }) => {
  const normalized = normalizeOption(option);
  const colSize = showMaxOutOfQuota ? 4 : 6;

  return (
    <Row className="g-2">
      <Col xs={colSize}>
        <Form.Label className="small mb-0">Età min</Form.Label>
        <Form.Control
          type="number"
          min={0}
          max={120}
          value={normalized.minAge ?? ''}
          onChange={(e) => onChange('minAge', e.target.value)}
          placeholder="es. 8"
        />
      </Col>
      <Col xs={colSize}>
        <Form.Label className="small mb-0">Età max</Form.Label>
        <Form.Control
          type="number"
          min={0}
          max={120}
          value={normalized.maxAge ?? ''}
          onChange={(e) => onChange('maxAge', e.target.value)}
          placeholder="es. 10"
        />
      </Col>
      {showMaxOutOfQuota && (
        <Col xs={colSize}>
          <Form.Label className="small mb-0">Max fuori fascia</Form.Label>
          <Form.Control
            type="number"
            min={0}
            max={20}
            value={normalized.maxOutOfQuota ?? ''}
            onChange={(e) => onChange('maxOutOfQuota', e.target.value)}
            placeholder="2"
          />
        </Col>
      )}
    </Row>
  );
};

/**
 * Editor della regola fuori quota danza, basata sul numero di componenti.
 * Mostra esempi calcolati in tempo reale per renderla immediatamente leggibile.
 */
const DanceQuotaRuleEditor = ({ rule, onChange }) => {
  const value = normalizeOutOfQuotaRule(rule, true);
  const rawRule = { ...DEFAULT_OUT_OF_QUOTA_RULE, ...(rule || {}) };
  const examples = [
    Math.max(0, value.minMembers - 1),
    value.minMembers,
    value.minMembers + value.stepSize,
    value.minMembers + value.stepSize * 2
  ];

  return (
    <div className="border rounded p-3 mb-3 bg-light">
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div>
          <h6 className="mb-1">
            <i className="bi bi-people me-1"></i>
            Regola fuori quota danza
          </h6>
          <small className="text-muted">
            Il numero di partecipanti fuori fascia ammessi dipende dal numero totale di componenti della formazione.
          </small>
        </div>
        <Form.Check
          type="switch"
          id="dance-quota-rule-enabled"
          label={value.enabled ? 'Attiva' : 'Disattivata'}
          checked={value.enabled}
          onChange={(e) => onChange('enabled', e.target.checked)}
        />
      </div>

      {value.enabled ? (
        <>
          <Row className="g-2">
            <Col xs={12} md={4}>
              <Form.Label className="small mb-0">Da quanti componenti</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={rawRule.minMembers}
                onChange={(e) => onChange('minMembers', e.target.value)}
              />
            </Col>
            <Col xs={12} md={4}>
              <Form.Label className="small mb-0">Fuori quota iniziali</Form.Label>
              <Form.Control
                type="number"
                min={0}
                value={rawRule.baseQuota}
                onChange={(e) => onChange('baseQuota', e.target.value)}
              />
            </Col>
            <Col xs={12} md={4}>
              <Form.Label className="small mb-0">Ogni quanti componenti in più</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={rawRule.stepSize}
                onChange={(e) => onChange('stepSize', e.target.value)}
              />
              <Form.Text className="text-muted">...si aggiunge 1 fuori quota.</Form.Text>
            </Col>
          </Row>

          <div className="small text-muted mt-2">
            Esempio: <strong>{examples[0]}</strong> componenti → {computeOutOfQuotaLimit(value, examples[0])} fuori quota
            {' · '}
            <strong>{examples[1]}</strong> → {computeOutOfQuotaLimit(value, examples[1])}
            {' · '}
            <strong>{examples[2]}</strong> → {computeOutOfQuotaLimit(value, examples[2])}
            {' · '}
            <strong>{examples[3]}</strong> → {computeOutOfQuotaLimit(value, examples[3])}
          </div>
          <div className="small text-muted mt-1">
            Sotto {value.minMembers} componenti non sono ammessi partecipanti fuori fascia
            (quindi Solisti e Duo restano senza fuori quota).
          </div>
        </>
      ) : (
        <div className="small text-muted">
          Regola disattivata: viene usato il valore &quot;Max fuori fascia&quot; impostato su ogni opzione.
        </div>
      )}
    </div>
  );
};

export default DanceQuotaRuleEditor;
